// src/components/frontpage/columns/ChatColumn.jsx
//
// Locality-context Chat column for the MultiViewGrid front page.
//
// Thin wrapper around the shared <ConcertinaThread variant="chat"> engine. This file now
// owns only the locality-specific chrome (column header + create button) and the locality
// data adapters (home_locality_id resolution, createOriginalChat / createResponse /
// getResponsesByRoot). All thread/accordion behaviour — single-open, one-indent replies,
// inline composers, like/dislike, inline edit, hybrid delete, gravestone, edited marker,
// #/chat/{uuid} permalink — lives in ConcertinaThread and is shared with article/event comments.

import React, { useCallback } from 'react';
import { useTheme } from '../../../themes/ThemeProvider';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAuth } from '../../../contexts/AuthContext';
import { chatService } from '../../../data/dataService';
import { baseParishId } from '../../../utils/scopeResolution';
import { supabaseFetch } from '../../../lib/supabase';
import ConcertinaThread from '../../chat/ConcertinaThread';
import ModeColumn from './ModeColumn';
import MobileActionLine from '../../core/HeadersAndFooters/MobileActionLine';

const ChatColumn = ({
  data = [],
  openThreadId = null,
  onThreadToggle,
  selectedArea,
  onChatsRefresh,
  hideColumnHeader = false,
  visitBoundaryId = null
}) => {
  const theme = useTheme();
  const { t } = useTranslation('chat');
  const { t: tFp } = useTranslation('frontpage');
  const { requireAuth, currentUser } = useAuth();

  // --- home_locality_id resolution (mirrors AddEditChat pattern) ---
  const resolveHomeLocalityId = useCallback(async () => {
    // An active visit (Change 3) wins — chat posted while visiting targets the visited locality,
    // never home (addendum-1 §A/§C).
    if (visitBoundaryId) {
      return Number(visitBoundaryId);
    }
    // The member's own parish in the area being browsed (the shared rule).
    const areaParish = baseParishId(selectedArea);
    if (areaParish != null) {
      return areaParish;
    }
    if (currentUser?.id) {
      try {
        const { data: homeArea } = await supabaseFetch.query('user_areas', {
          select: 'primary_boundary_id,boundary_ids',
          eq: { user_id: currentUser.id, is_home: true },
          single: true
        });
        const homeParish = baseParishId(homeArea);
        if (homeParish != null) return homeParish;
      } catch (err) {
        console.warn('Chat locality resolution failed:', err);
      }
    }
    return null;
  }, [selectedArea, currentUser, visitBoundaryId]);

  // --- Locality data adapters for the engine ---
  const loadResponses = useCallback((rootChatId) => chatService.getResponsesByRoot(rootChatId), []);

  const onCreateTopLevel = useCallback(async ({ headline, content }) => {
    const homeLocalityId = await resolveHomeLocalityId();
    if (!homeLocalityId) {
      const err = new Error('NO_LOCALITY');
      err.userMessage = t('error_no_locality_resolved');
      throw err;
    }
    await chatService.createOriginalChat({
      headline,
      content,
      community_id: null,
      home_locality_id: homeLocalityId,
      author_id: currentUser.id
    });
  }, [resolveHomeLocalityId, currentUser, t]);

  const onCreateReply = useCallback(async ({ parentChatId, rootChatId, content }) => {
    const rootThread = data.find(c => c.id === rootChatId);
    await chatService.createResponse({
      content,
      author_id: currentUser.id,
      parent_chat_id: parentChatId,
      community_id: null,
      home_locality_id: rootThread?.home_locality_id || null
    });
  }, [data, currentUser]);

  // --- Header + button just opens the engine's composer (also drives the mobile create pill) ---
  const handleNewChat = () => {
    requireAuth('gate_body_create_chat', () => {
      window.dispatchEvent(new CustomEvent('ournet:open-chat-composer'));
    });
  };

  // Pinned mobile sub-header — create + search, sibling above ModeColumn (not inside its
  // scrolling body, and not a ModeColumn child at all — ConcertinaThread sizes itself via its
  // own height:'100%' as ModeColumn's sole scrollBody={false} child, so the sub-header sits
  // outside that entirely rather than risk disturbing it). Retires the old shared
  // HeaderButtonsFeed Row 3 for this tab (mobile-tab-subheaders brief).
  const mobileSubHeader = (
    <MobileActionLine
      contextColor={theme.primary}
      createLabel={tFp('mobile_create_chat')}
      onCreate={handleNewChat}
      topBorder={false}
      sideBorders={false}
      searchPlaceholder={tFp('ui:search_articles')}
      onSearch={(term) => window.dispatchEvent(new CustomEvent('ournet:search-articles', { detail: { searchTerm: term } }))}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {hideColumnHeader && mobileSubHeader}
      <div style={{ flex: 1, minHeight: 0 }}>
        <ModeColumn
          title={tFp('column_chat')}
          themeColor={theme.primary}
          hideColumnHeader={hideColumnHeader}
          onAdd={handleNewChat}
          addLabel={tFp('new_chat')}
          scrollBody={false}
        >
          <ConcertinaThread
            variant="chat"
            data={data}
            loadResponses={loadResponses}
            onCreateTopLevel={onCreateTopLevel}
            onCreateReply={onCreateReply}
            onRefresh={onChatsRefresh}
            openThreadId={openThreadId}
            onThreadToggle={onThreadToggle}
            reportContentType="chat"
            emptyState={tFp('empty_chat')}
          />
        </ModeColumn>
      </div>
    </div>
  );
};

export default ChatColumn;
